import axios from "axios";
import { ChatMessage, ChatOneToOneConversation } from "../../data_models/chat";
import useAuth from "../../hooks/useAuth";
import { CHAT_ENDPOINTS } from "../../config/apiEndpoints";
import { UserId } from "../../data_models/helpers";
import { IEmployer } from "../../data_models/employer";
import { IFreelancer } from "../../data_models/freelancer";
import { uploadFileToSignedUrl } from "../../utils/uploadFiles";
import { FileMetadata } from "../../data_models/storage";
import { downloadFileFromSignedUrl } from "../../utils/downloadFiles";
import { MultiReadResult } from "../../data_models/db";

const useChatHelper = () => {

    const { authToken } = useAuth();

    const fetchAllPrivateConversations = async (page: number, limit: number): Promise<ChatOneToOneConversation[]> => {
        try {
            const response = await axios.get(CHAT_ENDPOINTS.GET_ALL_ONE_TO_ONE_CONVERSATIONS, {
                headers: {
                    Authorization: authToken,
                },
                params: {
                    page: page,
                    limit: limit
                }
            });
            return response.data || [];
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchPrivateConversation = async (conversationId: string): Promise<ChatOneToOneConversation> => {
        try {
            const response = await axios.get(CHAT_ENDPOINTS.GET_ONE_TO_ONE_CONVERSATION_BY_ID + '/' + conversationId,
                {
                    headers: {
                        Authorization: authToken,
                    },
                }
            )
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchConversationByPartner = async (partnerUserId: UserId): Promise<ChatOneToOneConversation> => {
        try {
            const response = await axios.get(CHAT_ENDPOINTS.GET_ONE_TO_ONE_CONVERSATION_BY_PARTNER_ID + '/' + partnerUserId.id + '/' + partnerUserId.userType,
                {
                    headers: {
                        Authorization: authToken,
                    },
                }
            )

            return response.data;

        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchPartnersByConversationId = async (conversationId: string): Promise<{ employers: IEmployer[], freelancers: IFreelancer[] }> => {

        try {
            const response = await axios.get(CHAT_ENDPOINTS.GET_PARTNERS_BY_CONVERSATION_ID + '/' + conversationId,
                {
                    headers: {
                        Authorization: authToken,
                    },
                }
            )
            return response.data || [];
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchPrivateMessages = async (conversationId: string, page: number, limit: number): Promise<MultiReadResult<ChatMessage>> => {
        try {
            const response = await axios.get(CHAT_ENDPOINTS.GET_MESSAGES_BY_CONVERSATION_ID + '/' + conversationId,
                {
                    headers: {
                        Authorization: authToken,
                    },
                    params: {
                        page: page,
                        limit: limit
                    }
                }
            )
            return response.data as MultiReadResult<ChatMessage>;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }




    const sendPrivateMessage = async (partnerId: UserId, content: string): Promise<ChatMessage> => {

        try {
            const response = await axios.post(CHAT_ENDPOINTS.POST_SEND_PRIVATE_MESSAGE,
                {
                    partnerId,
                    content,
                }, {
                headers: {
                    Authorization: authToken,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const sendPrivateMessageFile = async (partnerId: UserId, file: File): Promise<ChatMessage> => {
        try {
            const timestampPrefix = new Date().getTime().toString();

            const signedUrlResponse = await axios.get(CHAT_ENDPOINTS.GET_SIGNED_URL_UPLOAD_PRIVATE, {
                headers: {
                    Authorization: authToken,
                },
                params: {
                    contentType: file.type,
                    filename: file.name,
                    partnerId: partnerId.id,
                    partnerType: partnerId.userType,
                    timestampPrefix: timestampPrefix,
                }
            });
            const signedUrl = signedUrlResponse.data;

            // upload file to signfed URL
            await uploadFileToSignedUrl(file, signedUrl);

            // Confirm file upload
            const confirmResponse = await axios.post(CHAT_ENDPOINTS.POST_CONFIRM_UPLOAD_PRIVATE,
                {
                    partnerId,
                    meta: {
                        filename: file.name,
                        timestampPrefix: timestampPrefix,
                        fileSizeBytes: file.size,
                        contentType: file.type,
                        fileExtension: file.name.split('.').pop(),
                    }
                },
                {
                    headers: {
                        Authorization: authToken,
                    },
                }
            );

            return confirmResponse.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const downloadPrivateMessageFile = async (metadataId: string, conversationId: string, filename: string, targetLocation: string) => {
        try {
            const signedUrlResponse = await axios.get(CHAT_ENDPOINTS.GET_SIGNET_URL_DOWNLOAD_PRIVATE, {
                headers: {
                    Authorization: authToken,
                },
                params: {
                    metadataId,
                    conversationId
                },
            });
            const signedUrl = signedUrlResponse.data;
            await downloadFileFromSignedUrl(signedUrl, filename);

        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const markPrivateMessagesAsRead = async (messageIds: string[]) => {
        try {
            const response = await axios.put(`${CHAT_ENDPOINTS.POST_MARK_AS_READ}`,
                { messageIds },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                }
            );
            // no return value - updated data comes through sockets
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const notifyTyping = async (conversationId: string) => {
        try {
            const response = await axios.post(CHAT_ENDPOINTS.POST_NOTIFY_TYPING + '/' + conversationId,
                {},
                {
                    headers: {
                        Authorization: authToken,
                    },
                }
            );
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }
    return {
        fetchAllPrivateConversations,
        fetchPrivateConversation,
        fetchConversationByPartner,
        fetchPartnersByConversationId,
        fetchPrivateMessages,
        sendPrivateMessage,
        sendPrivateMessageFile,
        downloadPrivateMessageFile,
        markPrivateMessagesAsRead,
        notifyTyping,
    };
};

export default useChatHelper;